'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { getCookie } from 'cookies-next';
import { ContextDataService, ContextData, formatContextDataForDisplay, isContextDataComplete } from './contextDataService';

/**
 * CONTEXT MANAGER SYSTEM
 * 
 * This file provides a comprehensive context management system for the CareAscendium application.
 * It allows users to switch between different types of contexts (Audits, Incidents, etc.) and
 * maintains a history of recently accessed contexts for easy navigation.
 * 
 * Key Features:
 * - Context switching and management
 * - Context history tracking with localStorage persistence
 * - Context validation and formatting
 * - Type-safe context operations
 * - Utility functions for context manipulation
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Available context types in the system
 * These represent the different modules/features that can have contexts
 */
export type ContextType = 'Audit' | 'Committee Meeting' | 'Incident' | 'Grievance' | 'Training' | 'Policy' | 'Environmental Service' | 'Facility Assessment';

/**
 * Context tab types for UI display
 * These are the simplified versions used in the tab interface
 * Note: Not all ContextTypes have corresponding tab types
 */
export type ContextTabType = 'AUDIT' | 'COMMITTEE MEETING' | 'INCIDENT' | 'GRIEVANCE';

/**
 * Core context object structure
 * Represents a specific instance of a context (e.g., "Audit #123", "Incident #456")
 */
export interface ContextObject {
  /** The type of context (Audit, Incident, etc.) */
  contextType: ContextType;
  /** Unique identifier for this specific context instance */
  contextId: number | string;
  /** Optional human-readable title for the context */
  title?: string;
  /** Optional description of the context */
  description?: string;
  /** Optional facility ID associated with this context */
  facilityId?: string | number;
  /** Optional facility name for display purposes */
  facilityName?: string;
  /** Optional creation timestamp */
  createdAt?: string;
  /** Optional last update timestamp */
  updatedAt?: string;
  /** Optional additional metadata for extensibility */
  metadata?: Record<string, any>;
}

/**
 * State interface for the Context Manager
 * Contains all the state variables that the context manager manages
 */
export interface ContextManagerState {
  /** Currently active context (null if no context is selected) */
  currentContext: ContextObject | null;
  /** Array of recently accessed contexts (for history/navigation) */
  contextHistory: ContextObject[];
  /** Array of all available contexts that can be selected */
  availableContexts: ContextObject[];
  /** Currently loaded context data (detailed information about the current context) */
  currentContextData: ContextData | null;
  /** Loading state for async operations */
  isLoading: boolean;
  /** Loading state for context data fetching */
  isContextDataLoading: boolean;
  /** Error message if any operation fails */
  error: string | null;
  /** Error message for context data operations */
  contextDataError: string | null;
}

/**
 * Actions interface for the Context Manager
 * Contains all the functions that can be called to manipulate context state
 */
export interface ContextManagerActions {
  /** Set the current active context */
  setContext: (context: ContextObject) => void;
  /** Clear the current context (set to null) */
  clearContext: () => void;
  /** Add a context to the history without setting it as current */
  addToHistory: (context: ContextObject) => void;
  /** Remove a specific context from the history by ID */
  removeFromHistory: (contextId: string | number) => void;
  /** Clear all context history */
  clearHistory: () => void;
  /** Set the list of available contexts */
  setAvailableContexts: (contexts: ContextObject[]) => void;
  /** Find a context by its ID and optionally filter by type */
  getContextById: (contextId: string | number, contextType?: ContextType) => ContextObject | null;
  /** Get all contexts of a specific type */
  getContextsByType: (contextType: ContextType) => ContextObject[];
  /** Validate if a context object has required fields */
  validateContext: (context: Partial<ContextObject>) => boolean;
  /** Format context for API calls (converts to API parameter format) */
  formatContextForAPI: (context: ContextObject) => { p_context_type: string; p_context_id: string | number };
  /** Create context object from API response data */
  formatContextFromAPI: (contextType: string, contextId: string | number) => ContextObject;
  /** Fetch detailed data for the current context */
  fetchCurrentContextData: () => Promise<void>;
  /** Fetch detailed data for a specific context */
  fetchContextData: (context: ContextObject) => Promise<ContextData | null>;
  /** Clear the current context data */
  clearContextData: () => void;
  /** Refresh the current context data */
  refreshContextData: () => Promise<void>;
}

/**
 * Complete Context Manager interface
 * Combines state and actions into a single type for the context value
 */
export interface ContextManagerType extends ContextManagerState, ContextManagerActions {}

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

/**
 * Mapping between UI tab types and internal context types
 * Used to convert between the simplified tab interface and full context types
 */
export const CONTEXT_TYPE_MAP: Record<ContextTabType, ContextType> = {
  'AUDIT': 'Audit',
  'COMMITTEE MEETING': 'Committee Meeting',
  'INCIDENT': 'Incident',
  'GRIEVANCE': 'Grievance',
};

/**
 * Available tab options for the UI
 * These are the context types that have corresponding tabs in the interface
 */
export const CONTEXT_TAB_OPTIONS: ContextTabType[] = ['AUDIT', 'COMMITTEE MEETING', 'INCIDENT', 'GRIEVANCE'];

/**
 * Icon mappings for each context type
 * These correspond to icon names in the public/icons directory
 */
export const CONTEXT_ICONS: Record<ContextType, string> = {
  'Audit': 'audit-management',
  'Committee Meeting': 'committee-meeting',
  'Incident': 'incident-rca',
  'Grievance': 'grievance',
  'Training': 'training',
  'Policy': 'policy-and-procedure-management',
  'Environmental Service': 'environmental-service',
  'Facility Assessment': 'facility-assessment',
};

/**
 * Color mappings for each context type
 * Used for visual distinction in the UI (tabs, badges, etc.)
 */
export const CONTEXT_COLORS: Record<ContextType, string> = {
  'Audit': '#E91E63', // Pink
  'Committee Meeting': '#FF5722', // Deep Orange
  'Incident': '#FF9800', // Orange
  'Grievance': '#F44336', // Red
  'Training': '#9C27B0', // Purple
  'Policy': '#673AB7', // Deep Purple
  'Environmental Service': '#795548', // Brown
  'Facility Assessment': '#00BCD4', // Cyan
};

// ============================================================================
// REACT CONTEXT SETUP
// ============================================================================

/**
 * React Context for the Context Manager
 * This is the actual context that will hold the context manager state and actions
 */
const ContextManagerContext = createContext<ContextManagerType | undefined>(undefined);

/**
 * Custom hook to access the Context Manager
 * 
 * @returns The complete context manager state and actions
 * @throws Error if used outside of a ContextManagerProvider
 * 
 * @example
 * ```tsx
 * const { currentContext, setContext, contextHistory } = useContextManager();
 * ```
 */
export const useContextManager = (): ContextManagerType => {
  const context = useContext(ContextManagerContext);
  if (context === undefined) {
    throw new Error('useContextManager must be used within a ContextManagerProvider');
  }
  return context;
};

/**
 * Props interface for the Context Manager Provider
 */
interface ContextManagerProviderProps {
  /** Child components that will have access to the context manager */
  children: ReactNode;
  /** Maximum number of contexts to keep in history (default: 10) */
  maxHistorySize?: number;
}

// ============================================================================
// CONTEXT MANAGER PROVIDER COMPONENT
// ============================================================================

/**
 * Context Manager Provider Component
 * 
 * This is the main provider component that wraps the application and provides
 * context management functionality to all child components.
 * 
 * Features:
 * - Manages current context state
 * - Maintains context history with localStorage persistence
 * - Provides context manipulation functions
 * - Handles loading and error states
 * 
 * @param children - Child components that will have access to context manager
 * @param maxHistorySize - Maximum number of contexts to keep in history (default: 10)
 */
export const ContextManagerProvider: React.FC<ContextManagerProviderProps> = ({ 
  children, 
  maxHistorySize = 10 
}) => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  /** Currently active context (null if no context is selected) */
  const [currentContext, setCurrentContext] = useState<ContextObject | null>(null);
  /** Array of recently accessed contexts for navigation */
  const [contextHistory, setContextHistory] = useState<ContextObject[]>([]);
  /** Array of all available contexts that can be selected */
  const [availableContexts, setAvailableContexts] = useState<ContextObject[]>([]);
  /** Currently loaded context data (detailed information about the current context) */
  const [currentContextData, setCurrentContextData] = useState<ContextData | null>(null);
  /** Loading state for async operations */
  const [isLoading, setIsLoading] = useState(false);
  /** Loading state for context data fetching */
  const [isContextDataLoading, setIsContextDataLoading] = useState(false);
  /** Error message if any operation fails */
  const [error, setError] = useState<string | null>(null);
  /** Error message for context data operations */
  const [contextDataError, setContextDataError] = useState<string | null>(null);

  // ============================================================================
  // LOCAL STORAGE PERSISTENCE
  // ============================================================================

  /**
   * Load context history from localStorage on component mount
   * This ensures the user's context history persists across browser sessions
   */
  useEffect(() => {
    try {
      const storedHistory = localStorage.getItem('contextHistory');
      if (storedHistory) {
        const parsedHistory = JSON.parse(storedHistory);
        setContextHistory(parsedHistory);
      }
    } catch (err) {
      console.error('Error loading context history from localStorage:', err);
    }
  }, []);

  /**
   * Save context history to localStorage whenever it changes
   * This ensures the history is automatically persisted
   */
  useEffect(() => {
    try {
      localStorage.setItem('contextHistory', JSON.stringify(contextHistory));
    } catch (err) {
      console.error('Error saving context history to localStorage:', err);
    }
  }, [contextHistory]);


  // ============================================================================
  // CONTEXT MANAGEMENT FUNCTIONS
  // ============================================================================

  /**
   * Set the current active context
   * 
   * This function:
   * - Sets the provided context as the current active context
   * - Clears any existing error state
   * - Adds the context to history if it's not already present
   * - Maintains the history size limit
   * 
   * @param context - The context object to set as current
   */
  const setContext = useCallback((context: ContextObject) => {
    setCurrentContext(context);
    setError(null);
    
    // Add to history if not already present
    setContextHistory(prev => {
      const exists = prev.some(c => c.contextId === context.contextId && c.contextType === context.contextType);
      if (!exists) {
        const newHistory = [context, ...prev].slice(0, maxHistorySize);
        return newHistory;
      }
      return prev;
    });
  }, [maxHistorySize]);

  /**
   * Clear the current context
   * 
   * This function:
   * - Sets currentContext to null
   * - Clears any existing error state
   * - Does not affect the context history
   */
  const clearContext = useCallback(() => {
    setCurrentContext(null);
    setError(null);
  }, []);

  /**
   * Add a context to history without setting it as current
   * 
   * This is useful for adding contexts that were accessed but not necessarily
   * set as the active context.
   * 
   * @param context - The context object to add to history
   */
  const addToHistory = useCallback((context: ContextObject) => {
    setContextHistory(prev => {
      const exists = prev.some(c => c.contextId === context.contextId && c.contextType === context.contextType);
      if (!exists) {
        return [context, ...prev].slice(0, maxHistorySize);
      }
      return prev;
    });
  }, [maxHistorySize]);

  /**
   * Remove a specific context from history by its ID
   * 
   * @param contextId - The ID of the context to remove from history
   */
  const removeFromHistory = useCallback((contextId: string | number) => {
    setContextHistory(prev => prev.filter(c => c.contextId !== contextId));
  }, []);

  /**
   * Clear all context history
   * 
   * This removes all contexts from the history array.
   * Useful for resetting the user's context history.
   */
  const clearHistory = useCallback(() => {
    setContextHistory([]);
  }, []);

  /**
   * Set the list of available contexts
   * 
   * This function updates the availableContexts array with a new list of contexts.
   * This is typically called when contexts are loaded from an API.
   * 
   * @param contexts - Array of context objects that are available for selection
   */
  const setAvailableContextsAction = useCallback((contexts: ContextObject[]) => {
    setAvailableContexts(contexts);
  }, []);

  // ============================================================================
  // CONTEXT QUERY FUNCTIONS
  // ============================================================================

  /**
   * Find a context by its ID and optionally filter by type
   * 
   * @param contextId - The ID of the context to find
   * @param contextType - Optional context type to filter by
   * @returns The found context object or null if not found
   */
  const getContextById = useCallback((contextId: string | number, contextType?: ContextType): ContextObject | null => {
    const contexts = contextType 
      ? availableContexts.filter(c => c.contextType === contextType)
      : availableContexts;
    
    return contexts.find(c => c.contextId === contextId) || null;
  }, [availableContexts]);

  /**
   * Get all contexts of a specific type
   * 
   * @param contextType - The type of contexts to retrieve
   * @returns Array of context objects of the specified type
   */
  const getContextsByType = useCallback((contextType: ContextType): ContextObject[] => {
    return availableContexts.filter(c => c.contextType === contextType);
  }, [availableContexts]);

  // ============================================================================
  // CONTEXT VALIDATION AND FORMATTING
  // ============================================================================

  /**
   * Validate if a context object has the required fields
   * 
   * @param context - The context object to validate
   * @returns True if the context has required fields (contextType and contextId)
   */
  const validateContext = useCallback((context: Partial<ContextObject>): boolean => {
    return !!(context.contextType && context.contextId);
  }, []);

  /**
   * Format context for API calls
   * 
   * Converts a context object to the format expected by the API.
   * The API expects parameters with 'p_' prefix.
   * 
   * @param context - The context object to format
   * @returns Object with API-compatible parameter names
   */
  const formatContextForAPI = useCallback((context: ContextObject): { p_context_type: string; p_context_id: string | number } => {
    return {
      p_context_type: context.contextType,
      p_context_id: context.contextId,
    };
  }, []);

  /**
   * Create context object from API response data
   * 
   * Converts API response data back to a ContextObject.
   * 
   * @param contextType - The context type from the API
   * @param contextId - The context ID from the API
   * @returns A properly formatted ContextObject
   */
  const formatContextFromAPI = useCallback((contextType: string, contextId: string | number): ContextObject => {
    return {
      contextType: contextType as ContextType,
      contextId: contextId,
    };
  }, []);

  // ============================================================================
  // CONTEXT DATA FETCHING FUNCTIONS
  // ============================================================================

  /**
   * Fetch detailed data for the current context
   * 
   * This function fetches detailed information about the currently active context.
   * It automatically handles loading states and error management.
   */
  const fetchCurrentContextData = useCallback(async (): Promise<void> => {
    if (!currentContext) {
      console.log('🔍 ContextManager: No current context, clearing context data');
      setCurrentContextData(null);
      return;
    }

    console.log('🔍 ContextManager: Fetching context data for:', currentContext);
    setIsContextDataLoading(true);
    setContextDataError(null);

    try {
      const data = await ContextDataService.getContextDataFromObject(currentContext);
      console.log('🔍 ContextManager: Context data fetched successfully:', data);
      setCurrentContextData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch context data';
      console.error('🔍 ContextManager: Error fetching context data:', err);
      setContextDataError(errorMessage);
    } finally {
      setIsContextDataLoading(false);
    }
  }, [currentContext]);

  /**
   * Fetch detailed data for a specific context
   * 
   * This function fetches detailed information about any context object.
   * It does not update the current context data state.
   * 
   * @param context - The context object to fetch data for
   * @returns Promise containing the context data or null if failed
   */
  const fetchContextData = useCallback(async (context: ContextObject): Promise<ContextData | null> => {
    try {
      const data = await ContextDataService.getContextDataFromObject(context);
      return data;
    } catch (err) {
      console.error(`Error fetching data for ${context.contextType} ${context.contextId}:`, err);
      return null;
    }
  }, []);

  /**
   * Clear the current context data
   * 
   * This function clears the current context data without affecting the current context.
   * Useful when switching contexts or when data becomes stale.
   */
  const clearContextData = useCallback(() => {
    setCurrentContextData(null);
    setContextDataError(null);
  }, []);

  /**
   * Refresh the current context data
   * 
   * This function re-fetches the data for the current context.
   * Useful for ensuring data is up-to-date.
   */
  const refreshContextData = useCallback(async (): Promise<void> => {
    if (currentContext) {
      await fetchCurrentContextData();
    }
  }, [currentContext, fetchCurrentContextData]);

  // ============================================================================
  // AUTOMATIC CONTEXT DATA FETCHING
  // ============================================================================

  /**
   * Automatically fetch context data when current context changes
   * This ensures that detailed context information is always available
   */
  useEffect(() => {
    if (currentContext) {
      fetchCurrentContextData();
    } else {
      clearContextData();
    }
  }, [currentContext, fetchCurrentContextData, clearContextData]);

  // ============================================================================
  // CONTEXT VALUE ASSEMBLY
  // ============================================================================

  /**
   * Assemble the complete context value object
   * This combines all state and actions into a single object that will be
   * provided to all child components through the React context.
   */
  const value: ContextManagerType = {
    // State properties
    currentContext,
    contextHistory,
    availableContexts,
    currentContextData,
    isLoading,
    isContextDataLoading,
    error,
    contextDataError,
    
    // Action functions
    setContext,
    clearContext,
    addToHistory,
    removeFromHistory,
    clearHistory,
    setAvailableContexts: setAvailableContextsAction,
    getContextById,
    getContextsByType,
    validateContext,
    formatContextForAPI,
    formatContextFromAPI,
    fetchCurrentContextData,
    fetchContextData,
    clearContextData,
    refreshContextData,
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  /**
   * Render the context provider with the assembled value
   * All child components will have access to the context manager through this provider
   */
  return (
    <ContextManagerContext.Provider value={value}>
      {children}
    </ContextManagerContext.Provider>
  );
};

// ============================================================================
// HIGHER-ORDER COMPONENT
// ============================================================================

/**
 * Higher-order component for context management
 * 
 * This HOC wraps a component with the ContextManagerProvider, making it easy
 * to add context management to any component without manually wrapping it.
 * 
 * @param Component - The component to wrap with context management
 * @returns A new component that has access to the context manager
 * 
 * @example
 * ```tsx
 * const MyComponent = withContextManager(({ someProp }) => {
 *   const { currentContext, setContext } = useContextManager();
 *   // Component logic here
 * });
 * ```
 */
export const withContextManager = <P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> => {
  const WrappedComponent = (props: P) => (
    <ContextManagerProvider>
      <Component {...props} />
    </ContextManagerProvider>
  );
  
  WrappedComponent.displayName = `withContextManager(${Component.displayName || Component.name || 'Component'})`;
  
  return WrappedComponent;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Context utility functions
 * 
 * This object contains helper functions for working with contexts outside of
 * the React context system. These are pure functions that can be used
 * anywhere in the application.
 */
export const contextUtils = {
  /**
   * Get a human-readable display name for a context
   * 
   * @param context - The context object
   * @returns Display name (title if available, otherwise "Type #ID")
   */
  getDisplayName: (context: ContextObject): string => {
    return context.title || `${context.contextType} #${context.contextId}`;
  },

  /**
   * Get the icon name for a context type
   * 
   * @param contextType - The context type
   * @returns Icon name for the context type
   */
  getIcon: (contextType: ContextType): string => {
    return CONTEXT_ICONS[contextType] || 'assignment';
  },

  /**
   * Get the color for a context type
   * 
   * @param contextType - The context type
   * @returns Color hex code for the context type
   */
  getColor: (contextType: ContextType): string => {
    return CONTEXT_COLORS[contextType] || '#757575';
  },

  /**
   * Check if a context object is valid
   * 
   * @param context - The context object to validate
   * @returns True if the context has required fields
   */
  isValid: (context: Partial<ContextObject>): boolean => {
    return !!(context.contextType && context.contextId);
  },

  /**
   * Create a new context object
   * 
   * @param contextType - The type of context
   * @param contextId - The unique identifier
   * @param metadata - Optional additional metadata
   * @returns A new ContextObject
   */
  create: (contextType: ContextType, contextId: string | number, metadata?: Partial<ContextObject>): ContextObject => {
    return {
      contextType,
      contextId,
      ...metadata,
    };
  },

  /**
   * Convert a tab type to a context type
   * 
   * @param tabType - The tab type to convert
   * @returns The corresponding context type
   */
  tabTypeToContextType: (tabType: ContextTabType): ContextType => {
    return CONTEXT_TYPE_MAP[tabType];
  },

  /**
   * Convert a context type to a tab type
   * 
   * @param contextType - The context type to convert
   * @returns The corresponding tab type, or null if no tab exists
   */
  contextTypeToTabType: (contextType: ContextType): ContextTabType | null => {
    const entry = Object.entries(CONTEXT_TYPE_MAP).find(([_, value]) => value === contextType);
    return entry ? (entry[0] as ContextTabType) : null;
  },

  /**
   * Get all available context types
   * 
   * @returns Array of all context types
   */
  getAllContextTypes: (): ContextType[] => {
    return Object.values(CONTEXT_TYPE_MAP);
  },

  /**
   * Get all available tab types
   * 
   * @returns Array of all tab types
   */
  getAllTabTypes: (): ContextTabType[] => {
    return CONTEXT_TAB_OPTIONS;
  },
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

/**
 * Default export is the ContextManagerProvider
 * This allows for easy importing: import ContextManager from './ContextManager'
 */
export default ContextManagerProvider;
